import { formatArs } from "../../shared/money";
import { useState } from "react";
import { Link } from "react-router";
import type { Product } from "../../shared/types";
import { useCart } from "../cart/CartProvider";
import { useI18n } from "../i18n/I18nProvider";
import { imageUrl } from "../lib/api";
import { useComingSoon, useStoreSales } from "../lib/CatalogProvider";
import { rarityLabel } from "../../shared/rarity";
import { variantLabel } from "../../shared/variants";
import { CardFx } from "./CardFx";
import { Price } from "./Price";
import { TransferPrice } from "./TransferPrice";
import { StockBadge } from "./StockBadge";

export function ProductCard({ product }: { product: Product }) {
  const { t, pick, locale } = useI18n();
  const { add, open } = useCart();
  const [added, setAdded] = useState(false);
  const comingSoon = useComingSoon();
  // Closed, the store sells nothing of its own: only the market's offers count.
  const closed = !useStoreSales();
  const name = pick(product.name);
  const soldOut = closed || product.stock <= 0;
  // Community sellers offering it (search results): shown next to, or instead of, the store's stock.
  const offered = product.market && product.market.count > 0 ? product.market : null;
  const variants = product.variants ?? [];
  // A single sellable variant can go straight to the cart; several need the product page.
  const directVariant = variants.length === 1 ? variants[0] : null;
  const fromPrice = variants.length > 1 && new Set(variants.map((v) => v.price)).size > 1;
  const onSale = product.compareAtPrice != null && product.compareAtPrice > product.price;

  const handleAdd = () => {
    if (!directVariant) return;
    add(product, directVariant);
    setAdded(true);
    open();
    window.setTimeout(() => setAdded(false), 1500);
  };

  return (
    <article className={soldOut && !offered ? "product-card is-sold-out" : "product-card"}>
      <Link to={`/producto/${product.slug}`} className={product.energyType ? "product-card-media fx-host fx-square" : "product-card-media"}>
        <img src={imageUrl(product.imageKey)} alt={name} loading="lazy" width={400} height={400} />
        <CardFx type={product.energyType} />
        {!closed && (product.presale ? <span className="tag tag-presale">{t("product.presale")}</span> : onSale && <span className="tag tag-sale">{t("product.sale")}</span>)}
        {soldOut && !offered && !closed && <span className="tag tag-out">{t("product.outOfStock")}</span>}
        {soldOut && offered && <span className="tag tag-market">{t("offers.tag")}</span>}
      </Link>
      <div className="product-card-body">
        <h3 className="product-card-title">
          <Link to={`/producto/${product.slug}`}>{name}</Link>
        </h3>
        {(product.rarity || directVariant) && (
          <p className="product-card-meta">
            {rarityLabel(product.rarity, locale) && <span className="rarity-tag">{rarityLabel(product.rarity, locale)}</span>}
            {directVariant && product.category === "singles" && <span>{variantLabel(directVariant, locale)}</span>}
          </p>
        )}
        {soldOut && offered ? (
          // Only sellers have it: their price, not the store's (nobody can buy at that).
          <div className="price-row">
            <span className="price-from">{t("product.from")}</span>
            <span className="price">{formatArs(offered.minPriceArs, locale)}</span>
          </div>
        ) : closed ? (
          <p className="muted small">{t("closed.noOffers")}</p>
        ) : (
          <>
            <div className="price-row">
              {fromPrice && <span className="price-from">{t("product.from")}</span>}
              <Price price={product.price} priceArs={product.priceArs} compareAt={product.compareAtPrice} />
            </div>
            <TransferPrice price={product.price} priceArs={product.priceArs} />
          </>
        )}
        {offered ? (
          <p className="market-line">
            {soldOut ? null : <span className="muted">{t("offers.or")} </span>}
            <Link to={`/producto/${product.slug}#ofertas`}>{t("offers.tile", { price: formatArs(offered.minPriceArs, locale), count: offered.count })}</Link>
          </p>
        ) : (
          !closed && <StockBadge stock={product.stock} />
        )}
        {!comingSoon && soldOut && offered ? (
          <Link to={`/producto/${product.slug}#ofertas`} className="btn btn-outline btn-block">
            {t("offers.seeOffers", { count: offered.count })}
          </Link>
        ) : comingSoon ? (
          <button type="button" className="btn btn-soon btn-block" disabled>
            {t("soon.button")}
          </button>
        ) : closed ? (
          <Link to={`/producto/${product.slug}`} className="btn btn-outline btn-block">
            {t("closed.viewCard")}
          </Link>
        ) : !soldOut && !directVariant ? (
          <Link to={`/producto/${product.slug}`} className="btn btn-outline btn-block">
            {t("product.chooseOption")}
          </Link>
        ) : (
          <button type="button" className="btn btn-primary btn-block" disabled={soldOut || !directVariant} onClick={handleAdd}>
            {soldOut ? t("product.outOfStock") : added ? t("product.added") : product.presale ? t("product.reserve") : t("product.addToCart")}
          </button>
        )}
      </div>
    </article>
  );
}
